import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  getRecipe,
  normalizeRecipe,
  storeRecipes,
} from "../../../../utils/external-food-cache";
import { externalFoodErrorKey } from "../../../../utils/external-food-errors";
import ExternalFoodImage from "./ExternalFoodImage";

const FOOD_TYPE_I18N = {
  breakfast: "breakfast",
  "lunch/dinner": "lunchDinner",
  snack: "snack",
};

const MACRO_KEYS = ["proteins", "carbs", "fats", "fibers"];

function labelize(value) {
  return String(value)
    .split(/([\s-]+)/)
    .map((part) =>
      /^[\s-]+$/.test(part)
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
}

function formatMacro(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
}

function hasNutrition(recipe) {
  if (!recipe) return false;
  if (Number(recipe.kcal) > 0) return true;
  const nutrients = recipe.nutrients ?? {};
  return MACRO_KEYS.some((key) => Number(nutrients[key]) > 0);
}

function expandSteps(steps) {
  const out = [];
  for (const step of steps ?? []) {
    const text = String(step ?? "").trim();
    if (!text) continue;
    const parts = text
      .split(
        /(?<!\b(?:tbsp|tsp|oz|approx|min|lb|lbs|pt|qt|gal|kg|g|mg|c|fl)\.)\.\s+(?=[A-Z])/
      )
      .map((part) => part.trim())
      .filter(Boolean);
    for (const part of parts) {
      out.push(/[.!?]$/.test(part) ? part : `${part}.`);
    }
  }
  return out;
}

function BadgeSection({ title, items }) {
  if (!items?.length) return null;
  return (
    <section className="external-food-detail-section">
      <h3>{title}</h3>
      <div className="external-food-detail-badges">
        {items.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    </section>
  );
}

BadgeSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string),
};

function hasDetailFields(item) {
  if (!item || typeof item !== "object") return false;
  return Boolean(
    item.summary ||
      item.sourceUrl ||
      item.spoonacularSourceUrl ||
      item.displayDetailsComplete ||
      item.recipe?.summary ||
      item.recipe?.sourceUrl ||
      item.recipe?.spoonacularSourceUrl
  );
}

const ExternalFoodDetailModal = ({
  recipeId,
  initialRecipe = null,
  isFavorite = false,
  isPending = false,
  onClose,
  onToggleFavorite,
}) => {
  const { t } = useTranslation("coaching-portal");
  const ns = "masterFoodLibraryScreen.external";
  const detailNs = `${ns}.detail`;

  const [recipe, setRecipe] = useState(() => {
    const cached = initialRecipe || getRecipe(recipeId);
    return cached ? normalizeRecipe(cached) : null;
  });
  const [loading, setLoading] = useState(() => {
    const cached = initialRecipe || getRecipe(recipeId);
    return !(cached && (!isFavorite || hasDetailFields(cached)));
  });
  const [errorKey, setErrorKey] = useState(null);

  useEffect(() => {
    if (!recipeId) return undefined;

    // Check existing store/storage: open instantly if present (0 API calls, 0 credits)
    const cached = initialRecipe || getRecipe(recipeId);
    if (cached && (!isFavorite || hasDetailFields(cached))) {
      setRecipe(normalizeRecipe(cached));
      setLoading(false);
      setErrorKey(null);
      return undefined;
    }

    // Fallback: only if user arrived via direct link/refresh where recipe isn't in storage yet,
    // or if a favorite row lacks detail fields (summary, source link) and needs hydration
    let cancelled = false;
    const controller = new AbortController();

    setLoading(true);
    setErrorKey(null);
    if (cached) {
      setRecipe(normalizeRecipe(cached));
    } else {
      setRecipe(null);
    }

    const fetchDetails = async (path) => {
      const res = await fetch(path, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      const payload = await res.json().catch(() => ({}));
      return { res, payload };
    };

    const applyPayload = (res, payload) => {
      if (!res.ok) {
        setErrorKey(externalFoodErrorKey(res.status, payload));
        setRecipe(null);
        return false;
      }
      const nextRecipe = payload?.data?.recipe ?? null;
      if (!nextRecipe) {
        setErrorKey("generic");
        setRecipe(null);
        return false;
      }
      setErrorKey(null);
      const normalized = normalizeRecipe(nextRecipe);
      storeRecipes([normalized]);
      setRecipe(normalized);
      return true;
    };

    (async () => {
      try {
        const encodedId = encodeURIComponent(recipeId);
        if (isFavorite) {
          try {
            const favorite = await fetchDetails(
              `/actions/external-food/favorites/details/${encodedId}`
            );
            if (cancelled) return;
            if (applyPayload(favorite.res, favorite.payload)) return;
          } catch (err) {
            if (cancelled || err?.name === "AbortError") return;
          }
        }

        const regular = await fetchDetails(
          `/actions/external-food/details/${encodedId}`
        );
        if (cancelled) return;
        applyPayload(regular.res, regular.payload);
      } catch (err) {
        if (cancelled || err?.name === "AbortError") return;
        setErrorKey("generic");
        setRecipe(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [recipeId, initialRecipe, isFavorite]);

  if (!recipeId) return null;

  const dietaryLabels = (recipe?.diets ?? []).map(labelize);
  const cuisines = (recipe?.cuisines ?? []).map(labelize);
  const dishTypes = (recipe?.dishTypes ?? []).map(labelize);
  const ingredients = Array.isArray(recipe?.ingredients)
    ? recipe.ingredients.filter((item) => item?.name)
    : [];
  const steps = expandSteps(Array.isArray(recipe?.steps) ? recipe.steps : []);
  const nutrients = recipe?.nutrients ?? {};
  const sourceUrl = recipe?.sourceUrl || recipe?.spoonacularSourceUrl || null;
  const showHeroMeta = !loading && !errorKey && recipe;
  const showNutrition = hasNutrition(recipe);
  const foodTypeKey = FOOD_TYPE_I18N[recipe?.foodType];
  const foodTypeLabel = foodTypeKey
    ? t(`foodTypes.${foodTypeKey}`)
    : recipe?.foodType
      ? labelize(recipe.foodType)
      : null;
  const difficultyLabel = recipe?.difficulty
    ? t(`${detailNs}.difficultyLevels.${recipe.difficulty}`, {
        defaultValue: labelize(recipe.difficulty),
      })
    : null;
  const macrosBaseLabel = recipe?.macrosBase
    ? t(`macrosBaseTypes.${recipe.macrosBase}`, {
        defaultValue: labelize(recipe.macrosBase),
      })
    : null;

  return (
    <Modal
      className="external-food-detail-modal"
      width="52rem"
      canClose={false}
      dontRefreshOnClose
      onClose={onClose}
    >
      <div className="external-food-detail">
        <button
          type="button"
          className="external-food-detail-close"
          aria-label={t(`${detailNs}.close`)}
          onClick={onClose}
        >
          <img src="/img/new-icons/red-cross.svg" alt="" />
        </button>

        <div className="external-food-detail-hero">
          {recipe?.image ? (
            <ExternalFoodImage
              image={recipe.image}
              imageType={recipe.imageType}
              alt=""
            />
          ) : (
            <div className="external-food-detail-hero-empty" />
          )}
          <div className="external-food-detail-hero-fade" />
          <div className="external-food-detail-hero-content">
            <h2>
              {recipe?.title || (loading ? t(`${detailNs}.loading`) : "—")}
            </h2>
            {showHeroMeta && (
              <div className="external-food-detail-stats">
                <div className="external-food-detail-stats-left">
                  {recipe.readyInMinutes ? (
                    <span className="external-food-detail-stat">
                      <SvgIcon
                        src="/img/new-icons/vuesax-linear-clock.svg"
                        useCurrentColor
                        classNames="external-food-detail-stat-icon"
                      />
                      <span>
                        {t(`${ns}.minutes`, { count: recipe.readyInMinutes })}
                      </span>
                    </span>
                  ) : null}
                  {recipe.servings ? (
                    <span className="external-food-detail-stat">
                      <SvgIcon
                        src="/img/new-icons/people-circle-outline.svg"
                        useCurrentColor
                        classNames="external-food-detail-stat-icon"
                      />
                      <span>
                        {t(`${detailNs}.servings`, { count: recipe.servings })}
                      </span>
                    </span>
                  ) : null}
                  {difficultyLabel ? (
                    <span className="external-food-detail-stat">
                      <span>
                        {t(`${detailNs}.difficulty`)}: {difficultyLabel}
                      </span>
                    </span>
                  ) : null}
                  {foodTypeLabel ? (
                    <span className="external-food-detail-stat">
                      <span>{foodTypeLabel}</span>
                    </span>
                  ) : null}
                </div>
                <div className="external-food-detail-stats-right">
                  {sourceUrl ? (
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="external-food-detail-stat external-food-detail-stat-link"
                    >
                      <SvgIcon
                        src="/img/new-icons/link.svg"
                        useCurrentColor
                        classNames="external-food-detail-stat-icon"
                      />
                      <span>{t(`${detailNs}.viewSource`)}</span>
                    </a>
                  ) : null}
                  <button
                    type="button"
                    className="external-food-detail-stat external-food-detail-stat-button"
                    disabled={isPending}
                    aria-pressed={isFavorite}
                    onClick={() => onToggleFavorite?.(recipe)}
                  >
                    <SvgIcon
                      src={
                        isFavorite
                          ? "/img/new-icons/heart-solid.svg"
                          : "/img/new-icons/heart-outline.svg"
                      }
                      useCurrentColor
                      classNames={
                        isFavorite
                          ? "external-food-detail-stat-icon is-favorite"
                          : "external-food-detail-stat-icon"
                      }
                    />
                    <span>
                      {t(isFavorite ? `${ns}.unfavorite` : `${ns}.favorite`)}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="external-food-detail-body">
          {loading && (
            <p className="external-food-detail-status">
              {t(`${detailNs}.loading`)}
            </p>
          )}

          {!loading && errorKey && (
            <div className="external-food-detail-error">
              <p>{t(`${ns}.errors.title`)}</p>
              <span>{t(`${ns}.errors.${errorKey}`)}</span>
            </div>
          )}

          {!loading && !errorKey && recipe && (
            <>
              {showNutrition ? (
                <section className="external-food-detail-section">
                  <div className="external-food-detail-nutrition-header">
                    <h3>{t(`${detailNs}.nutrition`)}</h3>
                    {macrosBaseLabel ? (
                      <span className="external-food-detail-nutrition-base">
                        {macrosBaseLabel}
                      </span>
                    ) : null}
                  </div>
                  <div className="external-food-detail-nutrition">
                    <div className="external-food-detail-kcal">
                      <strong>
                        {recipe.kcal != null
                          ? Math.round(Number(recipe.kcal) || 0)
                          : "—"}
                      </strong>
                      <span>{t(`${detailNs}.kcal`)}</span>
                    </div>
                    <div className="external-food-detail-macros">
                      {MACRO_KEYS.map((key) => (
                        <div key={key} className="external-food-detail-macro">
                          <strong>{formatMacro(nutrients[key])}g</strong>
                          <span>{t(`${detailNs}.macros.${key}`)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              ) : null}

              {recipe.summary ? (
                <section className="external-food-detail-section">
                  <h3>{t(`${detailNs}.about`)}</h3>
                  <p>{recipe.summary}</p>
                </section>
              ) : null}

              {ingredients.length > 0 ? (
                <section className="external-food-detail-section">
                  <h3>{t(`${detailNs}.ingredients`)}</h3>
                  <ul className="external-food-detail-ingredients">
                    {ingredients.map((item, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: ingredients can contain duplicate names
                      <li key={`${item.name}-${index}`}>
                        <span className="external-food-detail-ingredient-name">
                          {labelize(item.name)}
                        </span>
                        {item.quantity ? (
                          <span className="external-food-detail-ingredient-qty">
                            {item.quantity}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {steps.length > 0 ? (
                <section className="external-food-detail-section">
                  <h3>{t(`${detailNs}.instructions`)}</h3>
                  <ol className="external-food-detail-steps">
                    {steps.map((step, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: recipe instruction steps are ordered strings without unique IDs
                      <li key={`step-${index}`}>{step}</li>
                    ))}
                  </ol>
                </section>
              ) : null}

              {(cuisines.length > 0 ||
                dishTypes.length > 0 ||
                dietaryLabels.length > 0) && (
                <>
                  <BadgeSection
                    title={t(`${detailNs}.dishTypes`)}
                    items={dishTypes}
                  />
                  <BadgeSection
                    title={t(`${detailNs}.dietary`)}
                    items={dietaryLabels}
                  />
                  <BadgeSection
                    title={t(`${detailNs}.cuisines`)}
                    items={cuisines}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

ExternalFoodDetailModal.propTypes = {
  recipeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialRecipe: PropTypes.object,
  isFavorite: PropTypes.bool,
  isPending: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func,
};

export default ExternalFoodDetailModal;
